import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PurchaseDocument, Supplier, InventoryItem, Warehouse } from '../types';
import { formatDate } from '../utils/dateFormat';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

const PurchaseDocuments: React.FC = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<PurchaseDocument[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState<PurchaseDocument | null>(null);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [selectedJsonData, setSelectedJsonData] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [formData, setFormData] = useState({
    number: '',
    date: new Date().toISOString().split('T')[0],
    supplierId: '',
    totalAmount: '',
    status: 'DRAFT' as 'DRAFT' | 'POSTED' | 'CANCELLED',
    notes: '',
    defaultWarehouseId: '',
    items: [] as Array<{
      inventoryItemId: string;
      quantity: number;
      unitPrice: number;
      totalPrice: number;
      warehouseId: string;
    }>
  });

  // Динамически обновляем общую сумму по табличной части
  useEffect(() => {
    const sum = formData.items.reduce((acc, it) => acc + (Number(it.totalPrice) || 0), 0);
    setFormData(prev => ({ ...prev, totalAmount: sum ? Number(sum.toFixed(2)).toString() : '0' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.items]);

  const round2 = (n: number | string) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  // Функции пагинации
  const handlePageChange = useCallback((page: number) => {
    fetchDocuments(page);
  }, [itemsPerPage]);

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, handlePageChange]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, handlePageChange]);

  // Загружаем сохраненные фильтры из localStorage
  useEffect(() => {
    const savedDateFrom = localStorage.getItem('purchaseDocuments_dateFrom');
    const savedDateTo = localStorage.getItem('purchaseDocuments_dateTo');
    
    if (savedDateFrom) {
      setDateFrom(savedDateFrom);
    }
    if (savedDateTo) {
      setDateTo(savedDateTo);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
    fetchInventoryItems();
    fetchWarehouses();
  }, []);

  // Загружаем данные только при монтировании компонента
  useEffect(() => {
    // Небольшая задержка, чтобы даты успели установиться из localStorage
    const timer = setTimeout(() => {
      fetchDocuments(1);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Загружаем данные при изменении страницы
  useEffect(() => {
    if (currentPage > 1) {
      fetchDocuments(currentPage);
    }
  }, [currentPage]);

  const fetchDocuments = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page: page,
        limit: itemsPerPage
      };
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      
      const data = await apiGet<{ purchases?: PurchaseDocument[]; pagination?: { pages: number } }>('/purchases', params);
      setDocuments(data.purchases || []);
      setTotalPages(data.pagination?.pages || 1);
      setCurrentPage(page);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки документов закупок');
      console.error('Ошибка:', err);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, dateFrom, dateTo]);

  const fetchSuppliers = async () => {
    try {
      const data = await apiGet<{ suppliers?: Supplier[] }>('/suppliers?limit=1000');
      setSuppliers(data.suppliers || []);
    } catch (err) {
      console.error('Ошибка загрузки поставщиков:', err);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const data = await apiGet<{ items?: InventoryItem[] }>('/inventory-items');
      setInventoryItems(data.items || []);
    } catch (err) {
      console.error('Ошибка загрузки ТМЦ:', err);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const data = await apiGet<Warehouse[]>('/warehouses');
      setWarehouses(data || []);
    } catch (err) {
      console.error('Ошибка загрузки складов:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        totalAmount: parseFloat(formData.totalAmount) || 0,
        items: formData.items.map(item => ({
          ...item,
          quantity: Number(item.quantity),
          unitPrice: Number(item.unitPrice),
          totalPrice: Number(item.totalPrice),
          warehouseId: formData.defaultWarehouseId || item.warehouseId || undefined
        }))
      };

      if (editingDocument) {
        await apiPut(`/purchases/${editingDocument.id}`, payload);
      } else {
        await apiPost('/purchases', payload);
      }
      
      await fetchDocuments(1);
      setShowForm(false);
      setEditingDocument(null);
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Ошибка сохранения документа закупки');
      console.error('Ошибка:', err);
    }
  };

  const handleEdit = (document: PurchaseDocument) => {
    setEditingDocument(document);
    setFormData({
      number: document.number,
      date: document.date.split('T')[0],
      supplierId: document.supplierId,
      totalAmount: document.totalAmount.toString(),
      status: document.status,
      notes: document.notes || '',
      defaultWarehouseId: document.defaultWarehouseId || '',
      items: document.items?.map(item => ({
        inventoryItemId: item.inventoryItemId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalPrice: item.totalPrice,
        warehouseId: item.warehouseId || ''
      })) || []
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить документ закупки?')) return;
    
    try {
      await apiDelete(`/purchases/${id}`);
      await fetchDocuments(currentPage);
    } catch (err: any) {
      setError(err.message || 'Ошибка удаления документа');
      console.error('Ошибка:', err);
    }
  };

  const handleShowJson = (document: PurchaseDocument) => {
    setSelectedJsonData(document);
    setShowJsonModal(true);
  };

  const handleCreateSupplierPayment = (document: PurchaseDocument) => {
    // Переходим на страницу оплат поставщикам с предзаполненными данными
    const params = new URLSearchParams({
      createFrom: document.id,
      purchaseType: 'inventory', // Указываем тип закупки
      supplierId: document.supplierId,
      amount: document.totalAmount.toString(),
      description: `Оплата по документу закупки ${document.number}`,
      date: document.date.split('T')[0] // Добавляем дату закупки
    });
    navigate(`/supplier-payments?${params.toString()}`);
  };

  const resetForm = () => {
    setFormData({
      number: '',
      date: new Date().toISOString().split('T')[0],
      supplierId: '',
      totalAmount: '',
      status: 'DRAFT',
      notes: '',
      items: []
    });
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        inventoryItemId: '',
        quantity: 0,
        unitPrice: 0,
        totalPrice: 0,
        warehouseId: prev.defaultWarehouseId || ''
      }]
    }));
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index: number, field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i === index) {
          const updated: any = { ...item };

          if (field === 'quantity') {
            updated.quantity = round2(value);
            updated.totalPrice = round2(Number(updated.quantity) * Number(updated.unitPrice));
          } else if (field === 'unitPrice') {
            updated.unitPrice = round2(value);
            updated.totalPrice = round2(Number(updated.quantity) * Number(updated.unitPrice));
          } else if (field === 'totalPrice') {
            updated.totalPrice = round2(value);
            if (Number(updated.quantity) > 0) {
              updated.unitPrice = round2(Number(updated.totalPrice) / Number(updated.quantity));
            }
          } else if (field === 'inventoryItemId') {
            updated.inventoryItemId = String(value);
          } else if (field === 'warehouseId') {
            updated.warehouseId = String(value);
          }
          return updated;
        }
        return item;
      })
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-100 text-gray-800';
      case 'POSTED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'Черновик';
      case 'POSTED': return 'Проведен';
      case 'CANCELLED': return 'Отменен';
      default: return status;
    }
  };

  // Функции для работы с фильтрами по датам
  const handleDateFromChange = (value: string) => {
    setDateFrom(value);
    localStorage.setItem('purchaseDocuments_dateFrom', value);
    // Не вызываем fetchDocuments автоматически - только при нажатии "Обновить"
  };

  const handleDateToChange = (value: string) => {
    setDateTo(value);
    localStorage.setItem('purchaseDocuments_dateTo', value);
    // Не вызываем fetchDocuments автоматически - только при нажатии "Обновить"
  };

  const handleResetFilters = () => {
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
    localStorage.removeItem('purchaseDocuments_dateFrom');
    localStorage.removeItem('purchaseDocuments_dateTo');
    fetchDocuments(1);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchDocuments(1);
  };

  if (loading) return <div className="flex justify-center items-center h-64">Загрузка...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">📦 Документы закупок ТМЦ</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/inventory-consumptions')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            📦 Расход ТМЦ
          </button>
          <button
            onClick={() => navigate('/inventory-returns')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            🔄 Возврат ТМЦ
          </button>
          <button
            onClick={() => navigate('/inventory-sales')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            💰 Продажа ТМЦ
          </button>
          <button
            onClick={() => navigate('/inventory-transfers')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            🔄 Перемещение ТМЦ
          </button>
          <button
            onClick={() => {
              resetForm();
              setEditingDocument(null);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            ➕ Добавить документ
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Фильтры по датам */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата с
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => handleDateFromChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Для навигации по месяцам используйте клавиши ← → или кликайте по датам"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата по
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => handleDateToChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Для навигации по месяцам используйте клавиши ← → или кликайте по датам"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
            >
              Сбросить
            </button>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
            >
              Обновить
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Номер
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Дата
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Поставщик
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Склад
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Сумма
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Статус
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {documents.map((document) => (
                <tr key={document.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {document.number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(document.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {document.supplier?.name || 'Не указан'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {document.defaultWarehouse?.name || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                    {Number(document.totalAmount).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MDL
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(document.status)}`}>
                      {getStatusText(document.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(document)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleCreateSupplierPayment(document)}
                      className="text-green-600 hover:text-green-900"
                      title="Создать оплату поставщику"
                    >
                      💳
                    </button>
                    <button
                      onClick={() => handleShowJson(document)}
                      className="text-gray-600 hover:text-gray-900"
                      title="Показать JSON"
                    >
                      📄
                    </button>
                    <button
                      onClick={() => handleDelete(document.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Удалить"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Пагинация */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Предыдущая
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Следующая
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Показано{' '}
                  <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>
                  {' - '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, documents.length + (currentPage - 1) * itemsPerPage)}
                  </span>
                  {' из '}
                  <span className="font-medium">{totalPages * itemsPerPage}</span>
                  {' результатов'}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Предыдущая</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Номера страниц */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // Показываем только несколько страниц вокруг текущей
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (
                      page === currentPage - 2 ||
                      page === currentPage + 2
                    ) {
                      return (
                        <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                  
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Следующая</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Модальное окно формы */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingDocument ? 'Редактировать документ' : 'Добавить документ закупки'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Номер документа *
                  </label>
                  <input
                    type="text"
                    value={formData.number}
                    onChange={(e) => setFormData(prev => ({ ...prev, number: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Оставьте пустым для авто-нумерации (INV-PUR-YYYY-####)"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Дата *
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Поставщик *
                  </label>
                  <select
                    value={formData.supplierId}
                    onChange={(e) => setFormData(prev => ({ ...prev, supplierId: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Выберите поставщика</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Статус
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="DRAFT">Черновик</option>
                    <option value="POSTED">Проведен</option>
                    <option value="CANCELLED">Отменен</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Общая сумма
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={Number(formData.totalAmount || 0).toFixed(2)}
                    onChange={(e) => setFormData(prev => ({ ...prev, totalAmount: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Склад
                  </label>
                  <select
                    value={formData.defaultWarehouseId}
                    onChange={(e) => setFormData(prev => ({ ...prev, defaultWarehouseId: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Выберите склад</option>
                    {warehouses.filter(w => w.isActive).map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name} {warehouse.isDefault ? '(по умолчанию)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Примечания
                </label>
                <input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Комментарий к документу"
                />
              </div>

              {/* Позиции документа */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Позиции документа
                  </label>
                  <button
                    type="button"
                    onClick={addItem}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                  >
                    ➕ Добавить позицию
                  </button>
                </div>
                
                {formData.items.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-2">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ТМЦ *
                        </label>
                        <select
                          value={item.inventoryItemId}
                          onChange={(e) => updateItem(index, 'inventoryItemId', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Выберите ТМЦ</option>
                          {inventoryItems.map(invItem => (
                            <option key={invItem.id} value={invItem.id}>
                              {invItem.name} ({invItem.unit})
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Количество *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Цена за единицу
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={Number(item.unitPrice ?? 0).toFixed(2)}
                          onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      {/* Убрали выбор склада построчно */}
                      
                      <div className="flex items-end space-x-2">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Сумма
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={Number(item.totalPrice ?? 0).toFixed(2)}
                            onChange={(e) => updateItem(index, 'totalPrice', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="bg-red-600 text-white px-2 py-2 rounded hover:bg-red-700"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingDocument(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingDocument ? 'Обновить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Модальное окно для отображения JSON */}
      {showJsonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                JSON содержимое документа
              </h3>
              <button
                onClick={() => setShowJsonModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="bg-gray-100 p-4 rounded-lg">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-auto">
                {JSON.stringify(selectedJsonData, null, 2)}
              </pre>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowJsonModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseDocuments;
